import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { RefreshCw, RotateCw, ChevronRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fr-FR");
};

const formatCurrency = (v) => {
  if (v === null || v === undefined || Number.isNaN(v)) return "-";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(v);
};

/**
 * Widget: contrats en tacite reconduction / préavis à surveiller
 */
export default function RenewalAlertsWidget({ horizonDays = 90, compact = false }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/api/engagements/renewals/upcoming?horizon_days=${horizonDays}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows(res.data.contracts || []);
    } catch (error) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [token, horizonDays]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleScan = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(
        `${API}/api/engagements/renewals/auto-flag?force=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        res.data?.flagged
          ? `${res.data.flagged} contrat(s) nouvellement flaggés en reconduction`
          : "Analyse terminée, aucun nouveau contrat détecté"
      );
      await fetchData();
    } catch (error) {
      toast.error("Erreur de scan");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse" data-testid="renewal-widget-loading">
        <div className="h-4 bg-slate-200 rounded w-1/3 mb-3"></div>
        <div className="h-16 bg-slate-100 rounded"></div>
      </div>
    );
  }

  if (rows.length === 0) {
    return null; // discret : ne rien afficher si aucun contrat à surveiller
  }

  // Sépare les 2 groupes
  const inRenewal = rows.filter((r) => r.en_reconduction);
  const preavisApproche = rows.filter((r) => !r.en_reconduction && r.jours_avant_preavis !== undefined && r.jours_avant_preavis <= horizonDays);

  return (
    <div className="bg-gradient-to-br from-purple-50 to-fuchsia-50 rounded-xl border border-purple-200 p-4" data-testid="renewal-alerts-widget">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <RotateCw className="w-5 h-5 text-purple-600" />
          <div>
            <h3 className="font-semibold text-slate-800">Contrats à surveiller</h3>
            <p className="text-xs text-slate-500">
              {inRenewal.length > 0 && `${inRenewal.length} en reconduction`}
              {inRenewal.length > 0 && preavisApproche.length > 0 && " · "}
              {preavisApproche.length > 0 && `${preavisApproche.length} préavis à venir sous ${horizonDays} j`}
            </p>
          </div>
        </div>
        <button
          onClick={handleScan}
          disabled={refreshing}
          className="text-slate-500 hover:text-purple-700 p-1"
          title="Relancer la détection"
          data-testid="renewal-widget-scan"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className={`space-y-2 ${compact ? "max-h-72 overflow-y-auto" : ""}`}>
        {rows.slice(0, compact ? 6 : 30).map((r) => (
          <button
            key={r.id}
            onClick={() => navigate(`/engagements/contrats/${r.id}`)}
            className="w-full text-left bg-white rounded-lg border border-purple-100 p-3 hover:border-purple-300 transition-colors"
            data-testid={`renewal-item-${r.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {r.en_reconduction && (
                    <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded font-medium">
                      🔁 En reconduction
                    </span>
                  )}
                  {!r.en_reconduction && r.jours_avant_preavis !== undefined && r.jours_avant_preavis <= 30 && (
                    <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-medium flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Préavis {r.jours_avant_preavis}j
                    </span>
                  )}
                  <span className="font-medium text-slate-800 truncate">
                    {r.nom_contrat || r.prestataire}
                  </span>
                </div>
                <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap gap-x-3">
                  {r.prestataire && r.nom_contrat && <span>{r.prestataire}</span>}
                  <span>Échéance: <strong>{formatDate(r.date_echeance)}</strong></span>
                  {r.date_limite_preavis && (
                    <span>Préavis avant: <strong>{formatDate(r.date_limite_preavis)}</strong></span>
                  )}
                  {r.montant_annuel_ht && <span>{formatCurrency(r.montant_annuel_ht)}/an</span>}
                </div>
                {r.nb_reconductions > 0 && (
                  <div className="text-xs text-purple-600 mt-0.5">
                    {r.nb_reconductions} reconduction{r.nb_reconductions > 1 ? "s" : ""} historique
                  </div>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 mt-1" />
            </div>
          </button>
        ))}
      </div>

      {compact && rows.length > 6 && (
        <button
          onClick={() => navigate("/engagements/contrats?tab=renewals")}
          className="w-full mt-3 text-xs text-purple-700 hover:text-purple-900 font-medium"
        >
          Voir tous les contrats ({rows.length})
        </button>
      )}
    </div>
  );
}
